import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";

// Define async thunks for service methods
interface FaqSectionResponse {
  data: any[]; // Replace `any[]` with the actual type if known
  totalItems: number;
}

export const fetchFaqs = createAsyncThunk<FaqSectionResponse, string>(
  "faq/fetchFaqs",
  async (sectionName: string, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await axios.get(`/papi/content/faqs/by-section-name?sectionName=${sectionName}`);
      const finalResponse = {
        ...response.data,
        totalItems: Number(response.headers["x-total-count"]),
      };
      return finalResponse;
    } catch (error) {
      handleApiError(error as AxiosError, "faq", dispatch);
      return rejectWithValue(
        error instanceof Error ? error.message : "An error occurred"
      );
    }
  }
);

export const fetchFaqSections = createAsyncThunk<FaqSectionResponse>(
  "faq/fetchFaqSections",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearError());
      const response = await axios.get(`/papi/content/faq-sections?sort=id,asc`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "faq", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : "An error occurred");
    }
  }
);

// Create slice
const faqSlice = createSlice({
  name: "faq",
  initialState: {
    sectionsInfo: {
      data: [] as any[], // Replace `any[]` with the actual type if known
      totalItems: 0,
    },
    faqListInfo: {
      data: [] as any[], // Replace `any[]` with the actual type if known
      totalItems: 0,
    },
    selectedSection: "",
    faqSectionsLoading: false,
    faqsLoading: false,
    error: null as string | null, // Ensure error is a string or null
  },
  reducers: {
    clearSection: (state) => {
      state.faqListInfo = {
        data: [],
        totalItems: 0,
      };
      state.sectionsInfo = {
        data: [],
        totalItems: 0,
      };
      state.selectedSection = "";
      state.faqSectionsLoading = false;
      state.faqsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqSections.pending, (state, action) => {
        state.faqSectionsLoading = true;
        state.error = null;
      })
      .addCase(fetchFaqSections.fulfilled, (state, action) => {
        state.faqSectionsLoading = false;
        state.sectionsInfo = {
          data: action.payload.data, // Ensure this matches the structure of FaqSectionResponse
          totalItems: action.payload.totalItems,
        } as FaqSectionResponse;
      })
      .addCase(fetchFaqSections.rejected, (state, action) => {
        state.faqSectionsLoading = false;
        state.error = action.error.message ?? null; // Ensure error is a string or null
      })
      .addCase(fetchFaqs.pending, (state, action) => {
        state.faqsLoading = true;
        state.error = null;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.faqsLoading = false;
        state.faqListInfo = {
          data: action.payload.data,
          totalItems: action.payload.totalItems,
        } as FaqSectionResponse;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.faqsLoading = false;
        state.error = action.payload as string; // Ensure payload is a string
      });
  },
});

export const { clearSection } = faqSlice.actions;

export default faqSlice.reducer;
